import { useState } from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useApolloClient, useMutation } from "@apollo/client";

import { Card, FilterValues, QuickFilterTypes, Table } from "@cofano/cck";

import { Box } from "@mui/material";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { DataFormat, Language, usePermission, hasPluginPermission } from "imports/Utils";

import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import { WeekRangePickerQuickFilter } from "components/cck/WeekRangePicker";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import CostTypeSelect from "view/costtypes/components/selects/CostTypeSelect";
import VatCodeSelect, { VatCodeSelectOption } from "view/vatcodes/components/selects/VatCodeSelect";
import { normalizeUnit } from "view/declarations/Declarations";
import InvoiceLineEditDialog from "view/invoicing/components/dialogs/InvoiceLineEditDialog";
import ApproveInvoiceLineDialog from "view/invoicing/components/dialogs/ApproveInvoiceLineDialog";

import { ContractLink, DossierLink, RelationLink, ShipLink } from "utils/LinkUtils";
import { getStoredRegionLocale } from "utils/regionUtils";

import { ChildPropsFromPagination } from "types/commons";

import { CharterType } from "__generated__/enums";
import { SearchInvoiceLinesDocument, type SearchInvoiceLinesQuery } from "__generated__/view/invoicing/graphql/invoicing.queries.generated";
import {
	BatchUpdateInvoiceLineUnexpectedDocument,
	DeleteInvoiceLineDocument,
	RevokeInvoiceLineApprovalDocument,
} from "__generated__/view/invoicing/graphql/invoicing.mutations.generated";

type InvoiceLineFromQuery = ChildPropsFromPagination<SearchInvoiceLinesQuery, "searchInvoicelines">

interface InvoiceLinesTableProps {
	tableId: TableId;
	charterType?: CharterType;
}

const InvoiceLinesTable = ({ tableId, charterType }: InvoiceLinesTableProps) => {

	const confirm = useConfirm();
	const client = useApolloClient();
	const { isAdmin, isFinance, isReadOnly } = usePermission();
	const session = useStoreState(store => store.session);

	const canApprove = (isAdmin || isFinance) && !isReadOnly;
	const disableInvoiceLineEditing = hasPluginPermission(session, "disableInvoiceLineEditing");

	const [ approveDialog, setApproveDialog ] = useState<{ open: boolean; invoiceLineIds: number[] }>({
		open: false,
		invoiceLineIds: [],
	});

	const [ invoiceLineDialog, setInvoiceLineDialog ] = useState<{ open: boolean; invoiceLine: InvoiceLineFromQuery | null }>({
		open: false,
		invoiceLine: null,
	});

	const { tableProps, refetch, refetchAfterDelete, filterValuesProps } = useBackendTable({
		id: tableId,
		selectSettings: {
			selectionEnabled: true,
			isFullSelectEnabled: false,
		},
		defaultFilter: [ {
			property: "approved",
			value: false,
		} ],
		onChange: async (pagination, sort, filters, callback) => {
			try {
				const result = await client.query({
					query: SearchInvoiceLinesDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort,
						},
						search: {
							creditors: filters.creditors?.map((it: RelationSelectOption) => it.id),
							debtors: filters.debtors?.map((it: RelationSelectOption) => it.id),
							ships: filters.ships?.map((it: ShipSelectOption) => it.id),
							weekRange: filters.weekNumber ? {
								from: moment(filters.weekNumber[0]).week(),
								to: filters.weekNumber[1] ? moment(filters.weekNumber[1]).week() : null,
							} : undefined,
							charter: filters.charter,
							contract: filters.contract,
							descriptions: filters.descriptions,
							costType: filters.costType?.id,
							vatCodes: filters.vatCodes?.map((it: VatCodeSelectOption) => it.id),
							approved: filters.approved,
							charterTypes: charterType ? [ charterType ] : undefined,
						},
					},
				});

				callback({
					content: result.data.searchInvoicelines?.content ?? [],
					totalResults: result.data.searchInvoicelines?.totalElements ?? 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ deleteInvoiceline ] = useMutation(DeleteInvoiceLineDocument, {
		onCompleted: () => refetchAfterDelete(),
	});

	const [ revokeApproval ] = useMutation(RevokeInvoiceLineApprovalDocument, {
		onCompleted: () => refetch(),
	});

	const [ batchUnexpected ] = useMutation(BatchUpdateInvoiceLineUnexpectedDocument, {
		onCompleted: () => refetch(),
	});

	const actions = [
		...(canApprove ? [ {
			title: intl.formatMessage({ id: "INVOICE_LINES_APPROVE" }),
			onClick: (selection: InvoiceLineFromQuery[] | null) => {
				setApproveDialog({
					open: true,
					invoiceLineIds: (selection ?? []).map(i => i.id),
				});
			},
		} ] : []),
		...(!isReadOnly && (isAdmin || isFinance) ? [ {
			title: intl.formatMessage({ id: "UNEXPECTED" }),
			onClick: (selection: InvoiceLineFromQuery[] | null) => {
				batchUnexpected({
					variables: {
						invoiceLineIds: (selection ?? []).map(it => it.id),
						isUnexpected: true,
					},
				});
			},
		} ] : []),
	];

	return (
		<>
			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						{filterValuesProps.values.length > 0 && (
							<FilterValues
								{...filterValuesProps}
								fields={[ {
									id: "creditors",
									title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
									renderFilterValue: (value: RelationSelectOption[]) => value.map(option => option.name).join(", "),
								}, {
									id: "debtors",
									title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
									renderFilterValue: (value: RelationSelectOption[]) => value.map(option => option.name).join(", "),
								}, {
									id: "ships",
									title: intl.formatMessage({ id: "SHIP" }),
									renderFilterValue: (value: ShipSelectOption[]) => value.map(option => option.name).join(", "),
								}, {
									id: "weekNumber",
									title: intl.formatMessage({ id: "CONTRACT_WEEK" }) + "#",
									renderFilterValue: (value: [ string | number, string | number | undefined ]) => `${moment(value[0]).week()} ${value[1] ? `- ${moment(value[1]).week()}` : ""}`,
								}, {
									id: "charter",
									title: intl.formatMessage({ id: "CHARTER" }),
								}, {
									id: "contract",
									title: intl.formatMessage({ id: "CONTRACT" }),
								}, {
									id: "descriptions",
									title: intl.formatMessage({ id: "DESCRIPTION" }),
								}, {
									id: "costType",
									title: intl.formatMessage({ id: "COST_TYPE" }),
									renderFilterValue: (value) => DataFormat.costType(value, getStoredRegionLocale()),
								}, {
									id: "vatCodes",
									title: intl.formatMessage({ id: "VAT_CODE" }),
									renderFilterValue: (value: VatCodeSelectOption[]) => value.map(option => option.label).join(", "),
								}, {
									id: "approved",
									title: intl.formatMessage({ id: "INVOICE_APPROVED" }),
									renderFilterValue: (value) => DataFormat.booleanAsText(value),
								} ]} />
						)}
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					actions={actions}
					rowActions={[ {
						icon: <RotateLeftIcon fontSize="small" />,
						tooltip: intl.formatMessage({ id: "INVOICE_LINE_WITHDRAW_APPROVAL" }),
						disabled: isReadOnly,
						hidden: (row: InvoiceLineFromQuery) => !row.approved || disableInvoiceLineEditing,
						onClick: (row) => {
							revokeApproval({
								variables: {
									invoiceLineIds: [ row.id ],
								},
							});
						},
					}, {
						icon: <EditIcon fontSize="small" />,
						disabled: isReadOnly,
						hidden: (row: InvoiceLineFromQuery) => !!row.approved || disableInvoiceLineEditing,
						onClick: (row: InvoiceLineFromQuery) => {
							setInvoiceLineDialog({
								open: true,
								invoiceLine: row,
							});
						},
					}, {
						icon: <DeleteIcon fontSize="small" />,
						hidden: isReadOnly || disableInvoiceLineEditing,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteInvoiceline({
									variables: {
										id: row.id,
									},
								});
							});
						},
					} ]}
					columns={[ {
						id: "creditors",
						title: Language("INVOICE_CREDITOR"),
						render: (row: InvoiceLineFromQuery) => <RelationLink relation={row.creditor} />,
						sortable: true,
						sortKey: "creditor.name",
						width: 150,
						filterComponent: (value, onChange) =>
							<RelationSelect
								multiple
								value={value}
								label={intl.formatMessage({ id: "INVOICE_CREDITOR" })}
								onChange={options => onChange(options)} />,
					}, {
						id: "debtors",
						title: Language("INVOICE_DEBTOR"),
						render: (row: InvoiceLineFromQuery) => <RelationLink relation={row.debtor} />,
						sortable: true,
						sortKey: "debtor.name",
						width: 150,
						filterComponent: (value, onChange) =>
							<RelationSelect
								multiple
								value={value}
								label={intl.formatMessage({ id: "INVOICE_DEBTOR" })}
								onChange={options => onChange(options)} />,
					}, {
						id: "ships",
						title: Language("SHIP"),
						render: (row: InvoiceLineFromQuery) => <ShipLink ship={row.ship} />,
						sortable: true,
						sortKey: "declaration.ship.name",
						width: 150,
						filterComponent: (value, onFilterChange) => (
							<ShipSelect
								label={intl.formatMessage({ id: "SHIP" })}
								onChange={ship => onFilterChange(ship)}
								multiple
								value={value} />
						),
					}, {
						id: "weekNumber",
						title: intl.formatMessage({ id: "CONTRACT_WEEK" }) + "#",
						width: 150,
						sortable: true,
						filterComponent: (value, onFilterChange) => (
							<WeekRangePickerQuickFilter
								label={intl.formatMessage({ id: "CONTRACT_WEEK" }) + "#"}
								value={value}
								onChange={onFilterChange} />
						),
					}, {
						id: "charter",
						title: intl.formatMessage({ id: "CHARTER" }),
						render: (row: InvoiceLineFromQuery) => <DossierLink charter={row.charter} />,
						defaultVisible: false,
						sortable: true,
						sortKey: "charter.charterNumber",
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "contract",
						title: intl.formatMessage({ id: "CONTRACT" }),
						render: (row: InvoiceLineFromQuery) => <ContractLink contract={row.charter?.contract} />,
						sortable: true,
						sortKey: "charter.contract.contractNumber",
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "descriptions",
						title: intl.formatMessage({ id: "DESCRIPTION" }),
						render: (row: InvoiceLineFromQuery) => row.description,
						sortKey: "description",
						sortable: true,
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "costType",
						title: Language("COST_TYPE"),
						render: (row: InvoiceLineFromQuery) => DataFormat.costType(row.costType, getStoredRegionLocale()),
						filterComponent: (value, onChange) => (
							<CostTypeSelect
								label={intl.formatMessage({ id: "COST_TYPE" })}
								value={value}
								onChange={onChange} />
						),
					}, {
						id: "price",
						title: Language("PRICE_PER_PIECE"),
						render: (row: InvoiceLineFromQuery) => DataFormat.price(row.price?.value, row.price?.currency?.isoCode),
					}, {
						id: "quantity",
						title: Language("INVOICE_QUANTITY"),
						render: (row: InvoiceLineFromQuery) => DataFormat.simpleUnit((row.unit == "%" ? 100 : 1) * (row.quantity ?? 0), normalizeUnit(row.unit)),
					}, {
						id: "vatCodes",
						title: Language("VAT_CODE"),
						render: (row: InvoiceLineFromQuery) => row.vatCode?.label,
						filterComponent: (value, onFilterChange) => (
							<VatCodeSelect
								label={intl.formatMessage({ id: "VAT_CODE" })}
								value={value}
								onChange={(options) => onFilterChange(options)}
								multiple />
						),
					}, {
						id: "agreement",
						title: Language("AGREEMENT"),
						render: (row: InvoiceLineFromQuery) => row.agreement && intl.formatMessage({ id: `AGREEMENT_TYPE_${row.agreement.type}` }),
					}, {
						id: "unexpected",
						title: Language("UNEXPECTED"),
						render: (row: InvoiceLineFromQuery) => row.unexpected ? Language("YES") : "",
					}, {
						id: "total",
						title: Language("TOTAL_EXCLUDE_VAT"),
						render: (row: InvoiceLineFromQuery) => DataFormat.price((row.price?.value ?? 0) * (row.quantity ?? 0), row.price?.currency?.isoCode),
					}, {
						id: "invoiceReference",
						sortable: true,
						title: Language("INVOICE_REFERENCE"),
					}, {
						id: "approved",
						title: intl.formatMessage({ id: "INVOICE_APPROVED" }),
						render: (row: InvoiceLineFromQuery) => DataFormat.boolean(row.approved),
						filterComponent: QuickFilterTypes.BOOLEAN,
					} ]} />
			</Card>

			{approveDialog.open && (
				<ApproveInvoiceLineDialog
					open={approveDialog.open}
					onClose={() => {
						setApproveDialog({
							open: false,
							invoiceLineIds: [],
						});
					}}
					invoiceLineIds={approveDialog.invoiceLineIds}
					refetch={refetch} />
			)}

			<InvoiceLineEditDialog
				open={invoiceLineDialog.open}
				onClose={() => setInvoiceLineDialog({ open: false, invoiceLine: null })}
				invoiceLine={invoiceLineDialog.invoiceLine}
				refetch={refetch} />
		</>
	);
};

export default InvoiceLinesTable;
